import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaCheckCircle, FaAward, FaLightbulb, FaHeart, FaRocket } from 'react-icons/fa';
import './WhyChoose.css';

const WhyChoose = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleReasons, setVisibleReasons] = useState([]);
  const sectionRef = useRef(null);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const reasons = useMemo(() => [
    {
      id: 1,
      icon: <FaAward />,
      title: 'נסיון רב שנים',
      description: '8 שנות נסיון מוכח בתחום הדיגיטל והרשתות החברתיות. עבדתי עם מגוון רחב של עסקים ובניתי להם נוכחות דיגיטלית מנצחת.',
      highlight: '8 שנות נסיון'
    },
    {
      id: 2,
      icon: <FaHeart />,
      title: 'שירות אישי',
      description: 'כל לקוח מקבל יחס אישי וליווי צמוד לאורך כל הדרך. אני זמינה לכל שאלה ומתאימה את השירות לצרכים הייחודיים של כל עסק.',
      highlight: 'יחס אישי'
    },
    {
      id: 3,
      icon: <FaLightbulb />,
      title: 'הבאת טרנדים חדשניים לעסק',
      description: 'תמיד מעודכנת בטרנדים האחרונים ברשתות החברתיות. אני מביאה רעיונות חדשים ויצירתיים שיבדלו את העסק שלכם מהתחרות.',
      highlight: 'טרנדים חדשניים'
    },
    {
      id: 4,
      icon: <FaRocket />,
      title: 'אופי צעיר, תוסס ומלא בהשראה',
      description: 'האנרגיה הצעירה שלי והתשוקה למקצוע משתקפות בכל פרויקט. אני מביאה רוח רעננה ומחדשת לעולם הדיגיטל של העסק שלכם.',
      highlight: 'אנרגיה צעירה'
    },
    {
      id: 5,
      icon: <FaCheckCircle />,
      title: 'תוצאות מוכחות ומדידות',
      description: 'כל הפרויקטים שלי מבוססים על נתונים ומטרות ברורות. אני מספקת דוחות שקופים ותוצאות מדידות שמוכיחות את הערך שאני מביאה.',
      highlight: 'תוצאות מוכחות'
    }
  ], []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      reasons.forEach((reason, index) => {
        setTimeout(() => {
          setVisibleReasons(prev => [...prev, reason.id]);
        }, index * 200);
      });
    }
  }, [isVisible, reasons]);

  return (
    <section id="why-choose" className="why-choose section" ref={sectionRef}>
      <div className="container">
        <div className={`why-choose-header ${isVisible ? 'fade-in' : ''}`}>
          <h2 className="heading-lg">
            <span className="highlight-yellow">5 סיבות</span> למה אני הטובה ביותר עבור העסק שלכם?
          </h2>
          <p className="text-lg">
            הנה מה שמבדיל אותי מכל השאר ומבטיח לכם הצלחה מרבית
          </p>
        </div>

        <div className="reasons-container">
          {reasons.map((reason, index) => (
            <div
              key={reason.id}
              className={`reason-card ${visibleReasons.includes(reason.id) ? 'visible' : ''}`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="reason-number">
                <span>{reason.id}</span>
              </div>
              
              <div className="reason-icon">
                {reason.icon}
              </div>

              <div className="reason-content">
                <h3 className="reason-title">
                  <span className="checkmark">✓</span>
                  {reason.title}
                </h3>
                
                <p className="reason-description">
                  {reason.description}
                </p>

                <div className="reason-highlight">
                  <span className="highlight-badge">{reason.highlight}</span>
                </div>
              </div>

              <div className="reason-glow"></div>
            </div>
          ))}
        </div>

        <div className={`why-choose-cta ${isVisible ? 'fade-in-delayed' : ''}`}>
          <div className="cta-content">
            <h3 className="cta-title">מוכנים להתחיל את המסע הדיגיטלי שלכם?</h3>
            <p className="cta-description">
              בואו נפגש לשיחה אישית ונבנה יחד את האסטרטגיה הדיגיטלית המושלמת עבור העסק שלכם
            </p>
            <button className="cta-button" onClick={scrollToContact}>
              <span>בואו נתחיל</span>
              <FaRocket />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
